import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, PlayCircle, Lock, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { KgValue } from '@/components/efs/KgValue';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import {
  AsyncStateBoundary,
  AuditIssueCard,
  LocalizedApiError,
  presentationForException,
} from '@/features/audit-issues';

import {
  useMassBalance,
  useRunReconciliation,
  useRequestClose,
  useCloseBatch,
} from './useMassBalance';
import type { MassBalanceViewDto, MaterialInventoryRow, SummaryCategoryRow } from './types';

const NS = 'mass-balance';

const SOURCE_BUCKETS: Array<{ key: string; i18n: string }> = [
  { key: 'opening_wip_kg', i18n: 'efs.mass_balance.buckets.opening_wip_kg' },
  { key: 'inbound_kg', i18n: 'efs.mass_balance.buckets.inbound_kg' },
  {
    key: 'materialized_and_shipped_kg',
    i18n: 'efs.mass_balance.buckets.materialized_and_shipped_kg',
  },
  {
    key: 'materialized_ending_inventory_kg',
    i18n: 'efs.mass_balance.buckets.materialized_ending_inventory_kg',
  },
  {
    key: 'secondary_process_transfer_kg',
    i18n: 'efs.mass_balance.buckets.secondary_process_transfer_kg',
  },
  {
    key: 'direct_material_shipment_kg',
    i18n: 'efs.mass_balance.buckets.direct_material_shipment_kg',
  },
  { key: 'ending_wip_kg', i18n: 'efs.mass_balance.buckets.ending_wip_kg' },
  { key: 'approved_loss_kg', i18n: 'efs.mass_balance.buckets.approved_loss_kg' },
];

const readinessTone = (readiness: string): 'default' | 'destructive' => {
  return readiness === 'blocked' ? 'destructive' : 'default';
};

export const MassBalancePage = () => {
  const { t } = useTranslation([NS, 'common', 'exceptions', 'issues']);
  const { batchId } = useParams();
  const user = useCurrentUser();
  const [searchParams] = useSearchParams();

  const view = useMassBalance(batchId);
  const run = useRunReconciliation();
  const requestClose = useRequestClose();
  const closeBatch = useCloseBatch();
  const [confirmingClose, setConfirmingClose] = useState(false);

  const canManage = hasPermission(user, 'mass_balance.manage');
  const canClose = hasPermission(user, 'monthly_close.approve');
  const showTechnicalDetails = canManage || hasPermission(user, 'mass_balance.read');

  // batch_mass_balance intents may highlight one exception or category.
  const highlightExceptionId = searchParams.get('exceptionId');
  const highlightCategory = searchParams.get('category');
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const loadedCount = view.data?.open_exceptions?.length ?? 0;
  useEffect(() => {
    if ((highlightExceptionId || highlightCategory) && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center' });
    }
  }, [highlightExceptionId, highlightCategory, loadedCount]);

  if (!batchId) {
    return (
      <PageHeader
        title={t('efs.mass_balance.title')}
        subtitle={t('efs.common.messages.no_batch_selected', { ns: 'common' })}
      />
    );
  }

  const renderView = (data: MassBalanceViewDto) => {
    const snapshot = data.latest_snapshot ?? null;
    const buckets = (snapshot?.source_buckets ?? {}) as Record<string, string | undefined>;
    const materialPayload = (snapshot?.material_inventory_payload ?? {}) as {
      materials?: MaterialInventoryRow[];
    };
    const summaryPayload = snapshot?.summary_comparison_payload as
      | {
          summary_total_kg?: string;
          detail_total_kg?: string;
          diff_kg?: string;
          categories?: SummaryCategoryRow[];
        }
      | null
      | undefined;
    const readiness = data.close_readiness ?? 'no_snapshot';
    const batchStatus = data.batch_status ?? '';
    // P1-#10: a batch in a terminal/locked state cannot be mutated by the
    // engine or transitioned further. Hide run/request_close/close in that case.
    const isLocked =
      batchStatus === 'closed' ||
      batchStatus === 'package_generated' ||
      batchStatus === 'report_issued';
    // close-only gate per spec 05 §9: spec requires pending_approval as the
    // starting state for a close action.
    const canShowClose =
      canClose &&
      !isLocked &&
      readiness !== 'blocked' &&
      batchStatus === 'pending_approval' &&
      !!snapshot;

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap justify-end gap-2">
          {canManage && !isLocked ? (
            <Button onClick={() => run.mutate(batchId)} disabled={run.isPending}>
              {run.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <PlayCircle className="mr-1 h-4 w-4" />
              )}
              {t('efs.mass_balance.actions.run')}
            </Button>
          ) : null}
          {canManage && !isLocked && readiness !== 'blocked' ? (
            <Button
              variant="outline"
              onClick={() => requestClose.mutate(batchId)}
              disabled={requestClose.isPending || !snapshot}
            >
              <Send className="mr-1 h-4 w-4" />
              {t('efs.mass_balance.actions.request_close')}
            </Button>
          ) : null}
          {canShowClose ? (
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmingClose) {
                  closeBatch.mutate(batchId);
                  setConfirmingClose(false);
                } else {
                  setConfirmingClose(true);
                }
              }}
              disabled={closeBatch.isPending || !snapshot}
            >
              <Lock className="mr-1 h-4 w-4" />
              {confirmingClose
                ? t('efs.mass_balance.messages.confirm_close')
                : t('efs.mass_balance.actions.close')}
            </Button>
          ) : null}
        </div>

        {/* Mutation failures stay visible beside the actions (spec-13 §9). */}
        {run.isError ? (
          <LocalizedApiError error={run.error} showTechnicalDetails={showTechnicalDetails} />
        ) : null}
        {requestClose.isError ? (
          <LocalizedApiError
            error={requestClose.error}
            showTechnicalDetails={showTechnicalDetails}
          />
        ) : null}
        {closeBatch.isError ? (
          <LocalizedApiError error={closeBatch.error} showTechnicalDetails={showTechnicalDetails} />
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>{t('efs.mass_balance.sections.close_readiness')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant={readinessTone(readiness)}>
              <AlertTitle>{t(`efs.mass_balance.readiness.${readiness}`)}</AlertTitle>
              <AlertDescription>
                {readiness === 'blocked'
                  ? t('efs.mass_balance.messages.blocked')
                  : readiness === 'ready_with_warnings'
                    ? t('efs.mass_balance.messages.warnings_only')
                    : readiness === 'no_snapshot'
                      ? t('efs.mass_balance.messages.no_snapshot')
                      : t('efs.mass_balance.messages.ready')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('efs.mass_balance.sections.source_side')}</CardTitle>
          </CardHeader>
          <CardContent>
            {snapshot ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('efs.mass_balance.fields.category')}</TableHead>
                    <TableHead className="text-right">kg</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {SOURCE_BUCKETS.map((b) => (
                    <TableRow key={b.key}>
                      <TableCell>{t(b.i18n)}</TableCell>
                      <TableCell className="text-right">
                        <KgValue value={buckets[b.key] ?? '0'} />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="font-semibold">
                    <TableCell>{t('efs.mass_balance.buckets.source_available_kg')}</TableCell>
                    <TableCell className="text-right">
                      <KgValue value={snapshot.source_available_kg} />
                    </TableCell>
                  </TableRow>
                  <TableRow className="font-semibold">
                    <TableCell>{t('efs.mass_balance.buckets.source_accounted_kg')}</TableCell>
                    <TableCell className="text-right">
                      <KgValue value={snapshot.source_accounted_kg} />
                    </TableCell>
                  </TableRow>
                  <TableRow className="font-semibold">
                    <TableCell>{t('efs.mass_balance.buckets.source_diff_kg')}</TableCell>
                    <TableCell className="text-right">
                      <KgValue value={snapshot.source_diff_kg} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-efs-text-muted">
                {t('efs.mass_balance.messages.no_snapshot')}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('efs.mass_balance.sections.material_inventory')}</CardTitle>
          </CardHeader>
          <CardContent>
            {materialPayload.materials && materialPayload.materials.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('efs.mass_balance.fields.material')}</TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.opening')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.production')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.adjustment_in')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.shipment')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.secondary')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.closing')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.loss')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.diff')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialPayload.materials.map((m) => (
                    <TableRow key={m.material_id}>
                      <TableCell>{m.material_name}</TableCell>
                      <TableCell className="text-right">
                        <KgValue value={m.opening_kg} />
                      </TableCell>
                      <TableCell className="text-right">
                        <KgValue value={m.production_kg} />
                      </TableCell>
                      <TableCell className="text-right">
                        <KgValue value={m.adjustment_in_kg} />
                      </TableCell>
                      <TableCell className="text-right">
                        <KgValue value={m.shipment_kg} />
                      </TableCell>
                      <TableCell className="text-right">
                        <KgValue value={m.secondary_kg} />
                      </TableCell>
                      <TableCell className="text-right">
                        <KgValue value={m.closing_kg} />
                      </TableCell>
                      <TableCell className="text-right">
                        <KgValue value={m.loss_kg} />
                      </TableCell>
                      <TableCell className="text-right">
                        <KgValue value={m.diff_kg} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-efs-text-muted">
                {t('efs.common.messages.empty', { ns: 'common' })}
              </p>
            )}
          </CardContent>
        </Card>

        {summaryPayload ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('efs.mass_balance.sections.summary_comparison')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('efs.mass_balance.fields.table')}</TableHead>
                    <TableHead>{t('efs.mass_balance.fields.category')}</TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.summary_kg')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.detail_kg')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.mass_balance.fields.diff')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(summaryPayload.categories ?? []).map((c) => {
                    const highlighted = !!highlightCategory && c.category === highlightCategory;
                    return (
                      <TableRow
                        key={`${c.table ?? '_'}::${c.category}`}
                        className={
                          highlighted ? 'bg-efs-bg-subtle ring-1 ring-efs-accent' : undefined
                        }
                      >
                        <TableCell className="text-xs text-efs-text-muted">
                          {c.table ? t(`efs.mass_balance.tables.${c.table}`) : '—'}
                        </TableCell>
                        <TableCell>
                          {highlighted ? (
                            <span ref={highlightRef as React.RefObject<HTMLSpanElement>}>
                              {c.category}
                            </span>
                          ) : (
                            c.category
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <KgValue value={c.summary_kg} />
                        </TableCell>
                        <TableCell className="text-right">
                          <KgValue value={c.detail_kg} />
                        </TableCell>
                        <TableCell className="text-right">
                          <KgValue value={c.diff_kg} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>{t('efs.mass_balance.sections.exceptions')}</CardTitle>
          </CardHeader>
          <CardContent>
            {data.open_exceptions.length > 0 ? (
              <div className="space-y-2">
                {data.open_exceptions.map((e) => {
                  const highlighted = highlightExceptionId === e.exception_id;
                  return (
                    <div key={e.exception_id} ref={highlighted ? highlightRef : undefined}>
                      <AuditIssueCard
                        presentation={presentationForException(e)}
                        statusLabel={t(`efs.exceptions.statuses.${e.status}`, {
                          ns: 'exceptions',
                        })}
                        showTechnicalDetails={showTechnicalDetails}
                        className={highlighted ? 'ring-2 ring-efs-accent' : undefined}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-efs-text-muted">
                {t('efs.exceptions.messages.no_open', { ns: 'exceptions' })}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t('efs.mass_balance.title')} subtitle={t('efs.mass_balance.subtitle')} />
      <AsyncStateBoundary
        isLoading={view.isLoading}
        isError={view.isError}
        error={view.error}
        isFetching={view.isFetching && !view.isLoading}
        data={view.data}
        onRetry={() => void view.refetch()}
      >
        {renderView}
      </AsyncStateBoundary>
    </div>
  );
};

export default MassBalancePage;
